import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

if (!customElements.get('mox-new-singles')) {
  class MoxNewSingles extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded === 'true') return;
      this.controller?.abort();
      this.controller = new AbortController();
      scrollContainerMediaQuery.addEventListener('change', this.observeNearby, {signal: this.controller.signal});
      if (this.awaitingAction) this.showAction(this.awaitingAction);
      else this.observeNearby();
    }
    observeNearby = () => {
      this.observer?.disconnect();
      if (this.loading || this.awaitingAction || this.dataset.loaded === 'true') return;
      const preloadDistance = navigator.connection?.saveData ? 200 : 400;
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.observer.disconnect();
          this.loadCards();
        }
      }, {root: getIntersectionRoot(), rootMargin: preloadDistance + 'px 0px'});
      this.observer.observe(this);
    };
    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      this.loading = false;
    }
    showAction(kind) {
      this.awaitingAction = kind;
      const status = this.querySelector('[data-singles-status]');
      if (!status || !this.isConnected) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button-secondary';
      button.textContent = kind === 'retry' ? this.dataset.retry : this.dataset.continue;
      button.addEventListener('click', () => {
        if (!this.loading) this.loadCards();
      }, {signal: this.controller.signal});
      status.replaceChildren(button);
      status.hidden = false;
    }
    async fetchPage(source, controller) {
      const request = new AbortController();
      let timedOut = false;
      const cancel = () => request.abort();
      controller.signal.addEventListener('abort', cancel, {once: true});
      const timeout = setTimeout(() => {
        timedOut = true;
        request.abort();
      }, 20000);
      try {
        const response = await fetch(source, {signal: request.signal, credentials: 'same-origin'});
        if (!response.ok) throw new Error('Source unavailable');
        return await response.text();
      } catch (error) {
        if (timedOut) throw new Error('Source timed out');
        throw error;
      } finally {
        clearTimeout(timeout);
        controller.signal.removeEventListener('abort', cancel);
      }
    }
    async loadCards() {
      const viewport = this.querySelector('[data-mox-viewport]');
      const status = this.querySelector('[data-singles-status]');
      if (!viewport || !status || this.loading || !this.isConnected || this.controller?.signal.aborted) return;
      this.loading = true;
      this.awaitingAction = null;
      this.observer?.disconnect();
      this.setAttribute('aria-busy', 'true');
      // Keep keyboard focus in the carousel when its action button is replaced.
      if (document.activeElement && status.contains(document.activeElement)) {
        viewport.focus({preventScroll: true});
      }
      status.textContent = this.dataset.loading;
      status.hidden = false;
      const controller = this.controller;
      const ids = this.loadedIds ||= new Set([...viewport.querySelectorAll('[data-single-id]')].map(card => card.dataset.singleId));
      let page = this.nextPage ?? 1;
      let pagesFetched = 0;
      try {
        const source = new URL(this.dataset.source, location.origin);
        if (source.origin !== location.origin) throw new Error('Unexpected origin');
        source.searchParams.set('view', 'mox-new-singles');
        source.searchParams.set('sort_by', 'created-descending');
        source.searchParams.set('filter.v.availability', '1');
        source.searchParams.set('filter.v.price.gte', '5.01');
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) source.searchParams.set('preview_theme_id', preview);
        // Bound automatic work; a user can continue without losing earlier cards.
        while (page && ids.size < 8 && pagesFetched < 5) {
          source.searchParams.set('page', String(page));
          const document = new DOMParser().parseFromString(await this.fetchPage(source, controller), 'text/html');
          if (controller.signal.aborted || !this.isConnected || this.controller !== controller) return;
          const result = document.querySelector('[data-mox-singles-page]');
          if (!result || result.dataset.sourceTheme !== this.dataset.themeId || result.dataset.sort !== 'created-descending') throw new Error('Unexpected theme');
          const additions = [];
          for (const card of result.querySelectorAll('[data-single-id]')) {
            if (ids.has(card.dataset.singleId)) continue;
            ids.add(card.dataset.singleId);
            additions.push(card);
            if (ids.size === 8) break;
          }
          const carousel = viewport.closest('moxcards-home-carousel');
          if (typeof carousel?.appendProgressiveCards === 'function') carousel.appendProgressiveCards(additions);
          else viewport.append(...additions);
          const next = Number(result.dataset.nextPage);
          page = Number.isInteger(next) && next > page ? next : 0;
          this.nextPage = page;
          pagesFetched++;
        }
        if (controller.signal.aborted || !this.isConnected || this.controller !== controller) return;
        if (page && ids.size < 8) {
          this.showAction('continue');
        } else {
          this.dataset.loaded = 'true';
          status.textContent = ids.size ? '' : this.dataset.empty;
          status.hidden = ids.size > 0;
        }
      } catch (error) {
        if (error.name !== 'AbortError' && !controller.signal.aborted && this.isConnected && this.controller === controller) {
          // Keep successful pages and retry the first page that did not complete.
          this.showAction('retry');
        }
      } finally {
        if (this.controller === controller) {
          this.loading = false;
          this.removeAttribute('aria-busy');
        }
      }
    }
  }
  customElements.define('mox-new-singles', MoxNewSingles);
}
